import React, { useCallback, useEffect, useState } from 'react';

import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Download } from 'lucide-react';
import Select, { ActionMeta, MultiValue, SingleValue } from 'react-select';
import makeAnimated from 'react-select/animated';

import { DownloadFormat } from '../../../../backend/src/types/types';
import Button from '@/components/atoms/Button';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { reactSelectStylesDownloadColumns, reactSelectStylesDownloadFormat } from '@/constants/styles';
import { MyOptionType } from '@/types';

const FORMAT_OPTIONS = [
  { label: 'Excel', value: DownloadFormat.EXCEL },
  { label: 'Docx', value: DownloadFormat.DOCX },
  { label: 'CSV', value: DownloadFormat.CSV },
];

interface Props {
  columnOptions: { label: string; value: string }[];
  downloadTitle: string;
  loadingDownload: boolean;
  totalCount?: number;
  showSourceDocumentsOption?: boolean;
  handleDownload: ({
    title,
    columns,
    downloadFormat,
    includeSourceDocuments,
  }: {
    title: string;
    columns: string[];
    downloadFormat: DownloadFormat;
    includeSourceDocuments?: boolean;
  }) => void;
}

export const DownloadTableModal = ({
  columnOptions,
  downloadTitle,
  loadingDownload,
  totalCount,
  showSourceDocumentsOption = false,
  handleDownload,
}: Props) => {
  const [title, setTitle] = useState(downloadTitle);
  const [columns, setColumns] = useState<MyOptionType[]>(columnOptions);
  const [downloadFormat, setDownloadFormat] = useState<MyOptionType>(FORMAT_OPTIONS[0]);
  const [includeSourceDocuments, setIncludeSourceDocuments] = useState(false);

  useEffect(() => {
    setTitle(downloadTitle);
  }, [downloadTitle]);

  const handleChangeTitle = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  }, []);

  const handleChangeSelectedColumns = useCallback(
    (newValue: MultiValue<MyOptionType> | SingleValue<MyOptionType>, actionMeta: ActionMeta<MyOptionType>) => {
      setColumns(Array.isArray(newValue) ? newValue : newValue ? [newValue] : []);
    },
    [],
  );

  const handleChangeSelectedFormat = useCallback(
    (newValue: SingleValue<MyOptionType>, actionMeta: ActionMeta<MyOptionType>) => {
      setDownloadFormat(newValue);
    },
    [],
  );

  const handleDownloadClick = () => {
    const columnValues = columns.map((column) => column?.value).filter((value) => value !== undefined) as string[];

    if (!downloadFormat) return;
    handleDownload({
      title,
      columns: columnValues,
      downloadFormat: downloadFormat.value as DownloadFormat,
      includeSourceDocuments,
    });
  };

  const animatedComponents = makeAnimated();

  return (
    <>
      {loadingDownload ? (
        <div className="flex h-72 flex-col items-center justify-center gap-8">
          <div className="text-xl font-medium text-gray-700">Preparing your download...</div>
          <FontAwesomeIcon icon={faCircleNotch} className="fa-spin text-brandSecondary text-4xl" />
        </div>
      ) : (
        <div className="px-8 py-6">
          {totalCount !== undefined && (
            <div className="mb-4 rounded-md bg-blue-50 p-3 text-sm text-blue-700">
              This download will include <span className="font-semibold">{totalCount}</span>{' '}
              {totalCount === 1 ? 'item' : 'items'}
            </div>
          )}
          <div className="mb-6">
            <div className="mb-4 flex flex-col gap-2">
              <Label htmlFor="title" className="text-sm font-medium text-gray-700">
                File Name
              </Label>
              <Input
                id="title"
                onChange={handleChangeTitle}
                value={title}
                placeholder="Enter a title for your file"
                className="w-full"
              />
            </div>
            <div className="mb-6 flex flex-col gap-2">
              <Label className="text-sm font-medium text-gray-700">Select Columns</Label>
              <Select
                options={columnOptions}
                components={animatedComponents}
                className="w-full rounded-md bg-white outline-none"
                styles={reactSelectStylesDownloadColumns}
                isMulti
                onChange={handleChangeSelectedColumns}
                value={columns}
                placeholder={'Choose columns to include'}
                maxMenuHeight={200}
              />
            </div>

            <div className="flex flex-col gap-1">
              <Label className="text-sm font-medium text-gray-700">Format</Label>
              <Select
                options={FORMAT_OPTIONS}
                className="w-full rounded-md bg-white outline-none"
                styles={reactSelectStylesDownloadFormat}
                onChange={handleChangeSelectedFormat}
                value={downloadFormat}
                placeholder={'Select format'}
                maxMenuHeight={200}
              />
            </div>

            {showSourceDocumentsOption && (
              <div className="mt-4 flex items-start gap-3">
                <input
                  type="checkbox"
                  id="includeSourceDocuments"
                  checked={includeSourceDocuments}
                  onChange={(e) => setIncludeSourceDocuments(e.target.checked)}
                  className="mt-1 h-4 w-4 cursor-pointer rounded border-gray-300 text-brandSecondary focus:ring-2 focus:ring-brandSecondary"
                />
                <label htmlFor="includeSourceDocuments" className="cursor-pointer text-sm text-gray-700">
                  <div className="font-medium">Include source documents</div>
                  <div className="text-xs text-gray-500">
                    Download a zip containing the chronology and all referenced documents
                  </div>
                </label>
              </div>
            )}

            <div className="my-6 border-t border-gray-200"></div>

            <Button
              onClick={handleDownloadClick}
              text="Download"
              type="primary"
              rounded="md"
              className="w-full justify-center py-2.5"
              icon={<Download className="mr-2 h-5 w-5" />}
            />
          </div>
        </div>
      )}
    </>
  );
};
